// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { isObservableArray, isObservable } from 'mobx';
import { curry, isNil, isNumber, cloneDeep, omit, isFunction, isPlainObject } from 'lodash/fp';
import { compose, withHandlers } from 'recompose';
import  {ICON_TRIANGLE, CATEGORY_TYPE } from 'logic/data/Izenda.PermissionConstants';

const INDETERMINATE_STATUS = 2;
const CHECKED_STATUS = 1;
const UNCHECK_STATUS = 0;

/**
 * Toggle section
 * @param {Object} props
 * @param {String} propsName
 * @param {Object} event
 */
const toggle = (props: any, propsName: string, event: any): void => {
  props.permissionStore[propsName] = !props.permissionStore[propsName];
};

/**
 * get Icon Class
 * @param  {object} props
 * @param  {string} propsName
 * @return {string} iconClass
 */
const getIconClass = (props: any, propsName: string): void => {
  return props.permissionStore[propsName] ? ICON_TRIANGLE.ICON_DOWN : ICON_TRIANGLE.ICON_RIGHT;
};

/**
 * Update section
 * @param {Object} props
 * @param {String} section
 * @param {Boolean} isLast
 */
const updateSectionCommon = (props: any, section: string, isLast: boolean): void => {
  if (!props.permissionStore.model.isClickedSection) {
    props.permission.updateSection(section);
  }

  if (isLast) {
    props.permissionStore.model.isClickedSection = false;
  }
};

/**
 * Get indeterminate
 * @param {Object} props
 * @param {Object} data
 * @returns {Boolean} indeterminate
 */
const getIndeterminateCommon = (props: any, data: any): boolean => {
  return data.tenantAccess === INDETERMINATE_STATUS;
};

/**
 * Update section
 * @param {Object} props
 * @param {String} section
 */
const getCheckedCommon = (props: any, data: any): boolean => {
  if (isNumber(data)) {
    return data === CHECKED_STATUS;
  } else {
    return data;
  }
};

/**
 * Check show group common
 * @param {Object} props
 * @param {String} data
 * @return {Boolean} showGroupStatus
 */
const checkShowGroupCommon = (props: any, data: any): boolean => {
  let  newData = isObservable(data) ? cloneDeep(data) : data;
  newData = omit(['propsCloned', 'section', 'isTenantSetup', 'cleanDirty', 'isDirty'], newData);

  let result = {
    isShowByTenantAccess: isNil(data.tenantAccess) ? false : data.tenantAccess !== UNCHECK_STATUS,
    isShowByOtherFields: false
  };

  /**
   * Check show group common
   * @param {Object} obj
   * @param {Object} result
   */
  const checkShowGroupByOtherFields = (obj: any, result: any): void => {
    for (let key in omit('tenantAccess', obj)) {
      if (!isFunction(obj[key]) && !isObservableArray(obj[key])) {
        if (isPlainObject(obj[key])) {
          checkShowGroupByOtherFields(obj[key], result);
        } else {
          result.isShowByOtherFields = result.isShowByOtherFields || obj[key];
        }
      }
    }
  };

  /**
   * Check show group by tenant access commonF
   * @param {Object} props
   * @param {Object} result
   */
  const checkShowGroupByTenantAccess = (obj: any, result: any): any => {
    if (obj.tenantAccess === UNCHECK_STATUS) return false;

    const checkTenantAccess = (result, obj) => {
      for (let key in obj) {
        if (isPlainObject(obj[key])) {
          checkTenantAccess(result, obj[key]);
        } else {
          if (key === 'tenantAccess') {
            result.isShowByTenantAccess = result.isShowByTenantAccess || obj[key] !== UNCHECK_STATUS;
          }
        }
      }
    };

    checkTenantAccess(result, omit('tenantAccess', obj));
  };

  checkShowGroupByTenantAccess(newData, result);
  checkShowGroupByOtherFields(newData, result);

  return result.isShowByTenantAccess && result.isShowByOtherFields;
};

/**
 * Permission Update Data
 * @param {Object} props
 * @param {Object} data
 * @param {String} value
 * @param {Object} event
 */
const permissionUpdateData = (props: any, data: any, value: string, event: any) => {
  props.permission.updateData(data, value, event.target.checked);
};

/**
 * Permission Update Tenant Access
 * @param {Object} props
 * @param {Object} data
 * @param {Object} value
 * @param {Object} event
 */
const permissionUpdateTenantAccess = (props: any, data: any, value: any, event: any) => {
  props.permission.updateTenantAccess(data, value, event.target.checked);
};

/**
 * Permission Update Tenant Access Parent
 * @param {Object} props
 * @param {Object} data
 * @param {Object} event
 */
const permissionUpdateTenantAccessParent = (props: any, data: any, event: any) => {
  props.permission.updateTenantAccessParent(data, event.target.checked);
};

const PermissionActions = compose(
  withHandlers({
    toggle: curry(toggle),
    getIconClass: curry(getIconClass),
    updateSectionCommon: curry(updateSectionCommon),
    getIndeterminateCommon: curry(getIndeterminateCommon),
    getCheckedCommon: curry(getCheckedCommon),
    checkShowGroupCommon: curry(checkShowGroupCommon),
    permissionUpdateData: curry(permissionUpdateData),
    permissionUpdateTenantAccess: curry(permissionUpdateTenantAccess),
    permissionUpdateTenantAccessParent: curry(permissionUpdateTenantAccessParent),
    getRolePermission(props: any) {
      return () => {
        if (!props.permissionStore.isTenantSetup || !isNil(props.permissionStore.tenantId)) {
          props.permissionStore.getRolePermission();
        }
      };
    },
    getCategoryAccessibility(props: any) {
      return () => {
        // get category accessibility for dashboard
        props.permissionStore.loadLazyCategory(
          props.permissionStore.dashboardCategoryAccessibility,
          CATEGORY_TYPE.DASHBOARD
        );
        // get category accessibility for report
        props.permissionStore.loadLazyCategory(
          props.permissionStore.reportCategoryAccessibility,
          CATEGORY_TYPE.REPORT
        );
      };
    }
  })
);

export default PermissionActions;



// WEBPACK FOOTER //
// ./ui/modules/Settings/PermissionTab/Izenda.PermissionActions.js